<template>
    <NuxtLink v-slot="{href, navigate}" :to="$withLocalePrefix(resourcePath)" custom external>
        <div
            :class="[
                'slice-content-grid--resources__card',
                {'slice-content-grid--resources__card--featured': variant === 'featured'},
                {'slice-content-grid--resources__card--compact': variant === 'compact'},
            ]"
            :data-testid="dataTestid"
            @click="navigate"
        >
            <div class="slice-content-grid--resources__card-image-wrapper">
                <slot name="collection-thumbnail-overlay" />
                <PrismicImage
                    v-if="image && isFilled.image(image)"
                    :field="image"
                    :alt="image.alt || ''"
                    class="slice-content-grid--resources__card-image"
                    :data-testid="`${dataTestid}-image`"
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, 600px"
                />
                <div class="slice-content-grid--resources__card-image-hover">
                    <span>{{ imageHoverText }}</span>
                </div>
            </div>

            <div class="slice-content-grid--resources__card-body">
                <MuiTag
                    :class="['slice-content-grid--resources__tag', `slice-content-grid--resources__tag--${resource.type}`]"
                    :data-testid="`${dataTestid}-tag`"
                >
                    {{ tagLabel }}
                </MuiTag>

                <a :href="href || ''">
                    <h3
                        v-if="isFilled.keyText(resource.data?.title)"
                        class="slice-content-grid--resources__card-title"
                        :data-testid="`${dataTestid}-title`"
                    >
                        {{ resource.data.title }}
                    </h3>
                </a>
                <PrismicRichText
                    v-if="isFilled.richText(resource.data?.excerpt)"
                    :field="resource.data.excerpt"
                    class="slice-content-grid--resources__card-excerpt"
                    :data-testid="`${dataTestid}-excerpt`"
                />

                <div class="slice-content-grid--resources__metadata" :data-testid="`${dataTestid}-metadata`">
                    <VJoyAvatar
                        v-if="authorImage && isFilled.image(authorImage)"
                        :photo-url="authorImage.url || ''"
                        :alt="authorImage.alt || ''"
                        size="medium"
                    />
                    <div class="slice-content-grid--resources__metadata-text">
                        <p v-if="authorName" class="slice-content-grid--resources__metadata-name">
                            {{ authorName }}
                        </p>
                        <p>
                            <span v-if="formattedDate">{{ formattedDate }}</span>
                            <template v-if="duration">
                                <span class="slice-content-grid--resources__metadata-separator" aria-hidden="true">•</span>
                                <span>{{ duration }}</span>
                            </template>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {ImageField} from '@prismicio/types';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import {NuxtLink} from '#components';
    import {MuiTag} from '#components';
    import {useFormat, useTranslation} from '#imports';
    import {VJoyAvatar} from '@maltjoy/core-vue';
    import type {ContentGridResource} from '../contentGridResources.types';

    type CardVariant = 'featured' | 'compact';

    const props = defineProps<{
        resource: ContentGridResource;
        variant: CardVariant;
        dataTestid: string;
    }>();

    const {t} = useTranslation();
    const articleData = computed(() => (props.resource.type === 'article' ? props.resource.data : null));
    const successStoryData = computed(() => (props.resource.type === 'successStory' ? props.resource.data : null));

    const authorImage = computed(() => articleData.value?.authorImage ?? successStoryData.value?.authorImage);
    const authorName = computed(() => {
        const name = articleData.value?.authorName ?? successStoryData.value?.authorName;
        return isFilled.keyText(name) ? name : undefined;
    });

    const resourcePath = computed(() => {
        const uid = props.resource.uid ?? props.resource.id;
        if (props.resource.type === 'article') return `/resources/article/${uid}`;
        if (props.resource.type === 'event') return `/resources/event/${uid}`;
        if (props.resource.type === 'guide') return `/resources/guide/${uid}`;
        if (props.resource.type === 'successStory') return `/resources/success-story/${uid}`;
        if (props.resource.type === 'collection') return `/resources/collection/${uid}`;
        return '/resources';
    });

    const tagLabel = computed(() => getTagTranslationKey(props.resource.type));

    function getTagTranslationKey(type: string) {
        switch (type) {
            case 'article':
                return t('resources.article');
            case 'collection':
                return t('resources.collection');
            case 'event':
                return t('resources.event');
            case 'guide':
                return t('resources.guide');
            default:
                return t('resources.successStory');
        }
    }

    const image = computed((): ImageField | undefined => {
        if (props.resource.type === 'article') {
            return props.resource.data?.thumbnail;
        }
        if (props.resource.type === 'event') {
            return props.resource.data?.eventImage;
        }
        if (props.resource.type === 'guide' || props.resource.type === 'successStory') {
            return props.resource.data?.thumbnail;
        }
        return undefined;
    });

    const duration = computed(() => {
        if (articleData.value && isFilled.keyText(articleData.value.duration)) {
            return articleData.value.duration;
        }
        return undefined;
    });

    const {isUpcomingEvent, formattedDate} = useDateHelpers();

    function useDateHelpers() {
        const {formatDate} = useFormat();

        const dateInput = computed(() => {
            if (props.resource.type === 'article' || props.resource.type === 'guide' || props.resource.type === 'successStory') {
                return props.resource.data?.releaseDate;
            }
            if (props.resource.type === 'event') {
                return props.resource.data?.eventStarts ?? props.resource.data?.date;
            }
            return undefined;
        });

        const formattedDate = computed(() => {
            const formatted = formatDate(dateInput.value, {year: 'numeric', month: 'short', day: 'numeric'});
            return formatted || undefined;
        });

        function isBefore(date1: Date, date2: Date): boolean {
            return date1.getTime() < date2.getTime();
        }

        function isSameDay(date1: Date, date2: Date): boolean {
            return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
        }

        const isUpcomingEvent = computed(
            (): boolean =>
                props.resource.type === 'event' &&
                (isBefore(new Date(), new Date(props.resource.data?.date as string)) ||
                    isSameDay(new Date(), new Date(props.resource.data?.date as string))),
        );

        return {
            isUpcomingEvent,
            formattedDate,
        };
    }

    const imageHoverText = computed(() => {
        if (props.resource.type === 'article' || props.resource.type === 'successStory') return t('resources.ctas.read');
        if (props.resource.type === 'event' && isUpcomingEvent.value) return t('resources.ctas.register');
        if (props.resource.type === 'event' && !isUpcomingEvent.value) return t('resources.ctas.see');

        return t('resources.ctas.discover');
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-content-grid--resources {
        &__card {
            cursor: pointer;
            display: flex;
            flex-direction: column;
            height: 100%;
            color: inherit;
            text-decoration: none;

            @include mq.screen_sm(min) {
                &:hover {
                    .slice-content-grid--resources__card-image {
                        filter: blur(3px);
                        -webkit-filter: blur(3px);
                    }
                    .slice-content-grid--resources__card-image-hover {
                        opacity: 1;
                    }
                }
            }

            &--compact {
                display: grid;
                grid-template-columns: 100px 1fr;
                gap: var(--joy-core-spacing-4);

                @include mq.screen_xs(min) {
                    grid-template-columns: 1fr 1fr;
                    gap: var(--joy-core-spacing-8);
                }
            }
        }

        &__card-image-wrapper {
            position: relative;
            overflow: hidden;
            border-radius: var(--joy-core-spacing-5);
            height: 100%;
            min-height: 141px;

            .slice-content-grid--resources__card--featured & {
                margin-bottom: var(--joy-core-spacing-5);

                @include mq.screen_md(min) {
                    margin-bottom: var(--joy-core-spacing-8);
                }
            }
        }

        &__card-image-hover {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotateZ(-6deg);
            border-radius: 40px;
            background-color: var(--joy-color-primary-50);
            z-index: 2;
            opacity: 0;
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);

            span {
                display: inline-block;
                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-5) var(--joy-core-spacing-0) var(--joy-core-spacing-5);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-primary-600);
                line-height: var(--joy-line-height-small);
                color: var(--joy-color-neutral-0);
            }
        }

        &__card-image {
            width: 100%;
            height: 100%;
            border-radius: var(--joy-core-spacing-5);
            object-fit: cover;
            aspect-ratio: 6 / 3;
            transition: filter var(--joy-transition-duration-default) var(--joy-transition-timing-function);
        }

        &__card-body {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        &__card-title {
            @include text.getFontBySize(secondary-200);
            color: var(--joy-color-neutral-70);

            @include mq.screen_md(min) {
                @include text.getFontBySize(secondary-400);
            }

            .slice-content-grid--resources__card--compact & {
                @include text.getFontBySize(primary-650);
            }
        }

        &__card-title,
        &__card-excerpt {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }

        &__card-excerpt {
            @include text.getFontBySize(primary-400);
            color: var(--joy-color-neutral-70);
        }

        &__tag {
            display: inline-block;
            text-transform: uppercase;
            margin-bottom: var(--joy-core-spacing-2);
            color: var(--joy-color-primary-50);
            background-color: var(--joy-color-primary-10);
            border: 1px solid var(--joy-color-primary-50);

            &--article.mui-c-tag {
                color: var(--joy-color-text-default);
                background-color: var(--joy-color-background-default);
                border: 1px solid var(--joy-color-neutral-50);
            }
        }

        &__metadata {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-3);

            .slice-content-grid--resources__card--featured & {
                padding-top: var(--joy-core-spacing-6);
            }

            .slice-content-grid--resources__card--compact & {
                display: none;

                @include mq.screen_sm(min) {
                    display: flex;
                    margin-top: var(--joy-core-spacing-2);
                    align-items: flex-start;
                    margin-top: auto;

                    @include mq.screen_md(min) {
                        align-items: center;
                    }

                    .joy-avatar__wrapper {
                        max-width: 48px;
                        min-width: 48px;
                        max-height: 48px;
                        min-height: 48px;
                    }
                }
            }
        }

        &__metadata-text {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-1);
            @include text.getFontBySize(primary-300);
        }

        &__metadata-name {
            font-weight: var(--joy-font-weight-bold);
        }

        &__metadata-separator {
            margin: 0 var(--joy-core-spacing-2);
        }
    }
</style>
